/**
 *  This file is maintained by Stencil, do not directly modify this file
 */
'use client';

import {enableStaticRendering} from 'mobx-react';

import {CommonData} from '@udemy-v2/common-data';
import {mergeWithArrayOverwrite} from '@udemy-v2/utils';
import React, {useEffect, useState} from 'react';
import {getClientAppContext} from '@udemy-v2/nextjs-core';
import {CommonProviders, CommonProvidersOptions} from '@udemy-v2/common-providers';
import {detectKeyboardNavigation} from '@udemy-v2/design-system-utils';
import {getPageKeyForPathname} from '@udemy-v2/nextjs-pagekeys';
import {pageKeyDefinitions} from '@/generated/pagekeys.config';
import {Vendors} from '@/components/vendors';

/**
 * Prevent observable components to re-render on the server. Other wise this causes a memory leak since lots of mobx
 * references being created on the server and never disposed.
 * @see https://github.com/mobxjs/mobx/tree/main/packages/mobx-react-lite#enablestaticrenderingenable-true
 */

enableStaticRendering(typeof window === 'undefined');

/**
 * Core options required for the application's providers
 */
// eslint-disable-next-line
export interface CoreProviderData extends CommonProvidersOptions {}

/**
 * Props for the CoreProviders component
 */
export interface CoreProvidersProps extends React.PropsWithChildren {
    data: CoreProviderData;
    isStaticRequest: boolean;
}

/**
 * Core providers component that sets up the application's context providers
 * Handles i18n, common data, tracking, and performance monitoring
 *
 * @component
 * @param {Readonly<CoreProvidersProps>} props - Component props with readonly constraint
 * @returns {JSX.Element} Provider-wrapped application content
 */
export function CoreProviders({children, data, isStaticRequest}: Readonly<CoreProvidersProps>) {
    const {
        experimentation,
        i18n,
        mediaPlayer,
        oneTrust,
        performance,
        shoppingList,
        smartBar,
        trackingContext,
    } = data;
    const [commonData, setCommonData] = useState<CommonData>(data.commonData);

    useEffect(() => {
        /**
         * Bootstraps client-side data when page has been statically generated
         * Fetches and merges client-specific common data with existing data
         * Only runs when isStaticRequest is true, indicating pre-rendered content
         */
        const bootstrapClient = async () => {
            const pageKey = getPageKeyForPathname({
                requestPathname: window.location.pathname,
                pageKeyDefinitions,
            });
            let clientCommonStaticData: CommonData = {
                page: {isLoading: false, pageKey},
            } as CommonData;
            // If this is a static request, we need to resolve full data.
            // Otherwise, it already is hydrated in `commonData` and we just need to merge it with ready state
            if (isStaticRequest) {
                const clientAppContext = await getClientAppContext();
                clientCommonStaticData = mergeWithArrayOverwrite(
                    clientAppContext,
                    clientCommonStaticData,
                );
            }
            const fullCommonData: CommonData = mergeWithArrayOverwrite(
                commonData,
                clientCommonStaticData,
            );

            // This core application data is ready (page.isLoading=false)
            // Ensure isLoading is always false after bootstrap completes
            // The /api/context/ endpoint returns isLoading: true, which was overwriting
            // the client's isLoading: false during the merge operation above.
            // This caused SmartBar, GTM, Sentry, and other components to never initialize
            // because they wait for page.isLoading === false.
            // By explicitly setting it here, we guarantee the page becomes interactive.
            fullCommonData.page = {
                ...fullCommonData.page,
                isLoading: false,
            };
            setCommonData(fullCommonData);
        };

        bootstrapClient();

        // Turn on keyboard navigation detection
        detectKeyboardNavigation();

        // Empty dependency array to run once on mount, ESLint rule disabled
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const options: CommonProvidersOptions = {
        commonData,
        experimentation,
        i18n,
        mediaPlayer,
        oneTrust,
        performance,
        shoppingList,
        smartBar,
        trackingContext,
    };

    return (
        <>
            <CommonProviders options={options}>
                <Vendors>{children}</Vendors>
            </CommonProviders>
        </>
    );
}
